<script setup>
import { computed, ref } from 'vue';
import { dynamicTime } from 'shared/helpers/timeHelper';

const props = defineProps({
  contactAttributes: {
    type: Object,
    default: () => ({}),
  },
});

const showAllSessions = ref(false);

// Sessions newest-first; pages keep chronological order within a session.
const sessions = computed(() => {
  const history = props.contactAttributes.visit_history;
  if (!Array.isArray(history)) return [];
  return [...history].reverse();
});

const visibleSessions = computed(() =>
  showAllSessions.value ? sessions.value : sessions.value.slice(0, 1)
);

const hiddenSessionCount = computed(() =>
  Math.max(sessions.value.length - 1, 0)
);

const sessionLabel = session =>
  dynamicTime(new Date(session.started_at).getTime() / 1000);

const sessionChip = session => {
  const source = session.source || 'direct';
  const medium = session.medium || 'none';
  return `${source} / ${medium}`;
};

const pageTitle = page => {
  if (page.title) return page.title;
  try {
    return new URL(page.url).pathname;
  } catch (e) {
    return page.url;
  }
};

// Dwell = gap between consecutive page entries within the session.
// The last page of a session has no next entry, so no dwell is shown.
const pageDwell = (session, index) => {
  const current = session.pages?.[index];
  const next = session.pages?.[index + 1];
  if (!current || !next) return '';
  const seconds = Math.round(
    (new Date(next.at).getTime() - new Date(current.at).getTime()) / 1000
  );
  if (seconds < 1) return '';
  if (seconds < 60) return `${seconds}s`;
  return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
};
</script>

<template>
  <div class="flex flex-col gap-4">
    <p v-if="!sessions.length" class="text-sm text-n-slate-11 mb-0">
      {{ $t('CONTACT_PANEL.VISIT_HISTORY.EMPTY') }}
    </p>
    <div
      v-for="(session, sessionIndex) in visibleSessions"
      :key="session.started_at"
      class="flex flex-col gap-1.5"
    >
      <div class="flex items-center justify-between gap-2">
        <span class="text-sm font-medium text-n-slate-12">
          {{ sessionLabel(session) }}
        </span>
        <span
          class="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-n-alpha-2 text-n-slate-11 whitespace-nowrap"
        >
          {{ sessionChip(session) }}
        </span>
      </div>
      <div
        v-for="(page, pageIndex) in session.pages"
        :key="`${sessionIndex}-${pageIndex}`"
        class="flex items-baseline justify-between gap-2 ltr:pl-2 rtl:pr-2 ltr:border-l rtl:border-r border-n-weak"
      >
        <a
          :href="page.url"
          rel="noopener noreferrer nofollow"
          target="_blank"
          class="text-sm text-n-slate-12 hover:text-n-brand truncate min-w-0"
          :title="page.url"
        >
          {{ pageTitle(page) }}
        </a>
        <span class="text-xs text-n-slate-11 whitespace-nowrap tabular-nums">
          {{ pageDwell(session, pageIndex) }}
        </span>
      </div>
    </div>
    <button
      v-if="hiddenSessionCount && !showAllSessions"
      class="text-xs text-n-brand text-left hover:underline"
      @click="showAllSessions = true"
    >
      {{
        $t('CONTACT_PANEL.VISIT_HISTORY.SHOW_PREVIOUS', {
          count: hiddenSessionCount,
        })
      }}
    </button>
  </div>
</template>
